import { useEffect, useMemo, useState } from 'react'

import styles from './style.module.scss'
import FilterPanel from '../../FilterPanel'
import useFilters from '../../FilterPanel/useFilters'
import { branchField, companyTypeField, schoolField, schoolTypeField, stateField } from '../../FilterPanel/fields'
import Pagination from '../../Pagination'
import Search from '../../Search'
import BtnFilter from '../../BtnFilter'
import { Link } from 'react-router-dom'
import { TApi, TRoutes } from '../../../shared/const'
import http from '../../../shared/http'
import SingleData from '../../Widget/singleData'
import CardCompany from '../../../components/views/common/CardCompany'
import { useTranslation } from 'react-i18next'
import LimitFilterButton from '../../LimitFilterButton'
import { connect } from 'react-redux'
import useDebouncedValue from '../../../shared/useDebouncedValue'
import useLatestOnly from '../../../shared/useLatestOnly'
import PublicFooter from '../../../components/PublicFooter'
import PublicTopBar from '../../../components/PublicTopBar'
import NewsBanner from '../../../components/NewsBanner'

/*
 * Oeffentliche Startseite: Kopfleiste (Marke, Menue, Suche/Filter/Anmelden —
 * `components/PublicTopBar`, geteilt mit der Kursseite), der wechselnde
 * Aufmacher (Skills4Retail und Junior-Bilanz), 5 Statistik-Widgets, Firmenkarten im 5er-Raster (analog zu den
 * Widgets), Fusszeile mit den Rechtsseiten.
 */
function CompaniesPresentation(props) {
	const { t, i18n } = useTranslation()
	const [companies, setCompanies] = useState([])
	const [statistic, setStatistic] = useState([])
	const [countAll, setCountAll] = useState(0)
	const [page, setPage] = useState(0)
	const [search, setSearch] = useState('')
	const [showFilter, setShowFilter] = useState(false)
	const [loading, setLoading] = useState(false)
	const [limit, setLimit] = useState(30)

	const options = [
		{ value: 30, label: '30' },
		{ value: 60, label: '60' },
		{ value: 90, label: '90' },
		{ value: 120, label: '120' },
		{ value: 150, label: '150' }
	]

	const fetchStatistic = () => {
		http.get(TApi.COMMON_GET_STATS).then(({ data }) => {
			setStatistic(data)
		})
	}

	const fields = useMemo(
		() => [
			stateField(t),
			companyTypeField(t),
			branchField(t, props.branch, i18n.language),
			schoolTypeField(t, props.schoolTypes),
			schoolField(t, { isPublic: true })
		],
		[t, i18n.language, props.branch, props.schoolTypes]
	)

	const filters = useFilters({ storageKey: 'company-presentation', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	const fetchAllCompanies = () => {
		const isCurrent = latest()

		setLoading(true)
		http.get(TApi.COMMON_GET_COMPANIES, {
			params: {
				limit,
				offset: page * limit,
				search: debouncedSearch,
				filter: filterQuery
			}
		})
			.then(({ data }) => {
				if (!isCurrent()) return

				setCompanies(data.allCompanies)
				setCountAll(data.count)
			})
			.finally(() => setLoading(false))
	}

	const handleChangeLimit = selectedOption => {
		setLimit(selectedOption.value)
	}

	// Gefiltert und gesucht wird ohne Knopfdruck; jede Aenderung setzt die Seite
	// zurueck, sonst stuende man auf Seite 4 einer kuerzer gewordenen Liste.
	useEffect(() => {
		fetchAllCompanies()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [limit, page, debouncedSearch, filterQuery])

	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearchChange = value => {
		setPage(0)
		setSearch(value)
	}

	useEffect(() => {
		fetchStatistic()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	/*
	 * Eine Kachel mit der Zahl 0 sagt nichts — zu Schuljahresbeginn stehen
	 * „Unternehmen“ und „Schüler:innen“ dort, bevor die erste Junior
	 * Company gegründet ist. Solche Zähler bleiben weg; die übrigen teilen
	 * sich dafür die volle Breite (siehe `widgetsContainer` im Stylesheet).
	 */
	const zaehlerMitBestand = statistic.filter(el => Number(el.total) > 0)
	return (
		<div className={styles.root}>
			<PublicTopBar>
				<Search
					value={search}
					placeholder={t('Search companies...')}
					onChange={e => handleSearchChange(e.target.value)}
					onSubmit={e => e.preventDefault()}
				/>
				<BtnFilter loading={loading} icon="arrow" onClick={() => setShowFilter(true)} count={filters.count}>
					{t('Filter')} ({filters.count})
				</BtnFilter>
			</PublicTopBar>

			{/*
			 * Erste Zeile unter der Kopfleiste: Wer die Seite oeffnet, soll zuerst
			 * sehen, dass es hier etwas zu lernen gibt — und was im letzten
			 * Schuljahr entstanden ist. Der Aufmacher wechselt zwischen beidem.
			 */}
			<NewsBanner />

			{filters.count === 0 && zaehlerMitBestand.length > 0 && (
				<div className={styles.widgetsContainer}>
					{zaehlerMitBestand.map(el => (
						<SingleData key={el.name} header={el.name} data={el.total} />
					))}
				</div>
			)}
			<div className={styles.subHeader}>
				<Pagination limit={limit} page={page} label={t('companies')} countAll={countAll} onChange={setPage} />
				<LimitFilterButton limit={limit} options={options} onChange={handleChangeLimit} />
			</div>

			<div className={styles.companiesContainer}>
				{companies.map(company => (
					<CardCompany company={company} className={styles.companyCard} />
				))}
				{/*
				 * Schuljahresstart: Gibt es (ohne Suche und Filter) noch gar keine
				 * Junior Company, zeigt die Seite statt eines leeren Rasters eine
				 * Einladung — der Weg zur Gruendung fuehrt ueber die Registrierung.
				 */}
				{!loading && countAll === 0 && filters.count === 0 && !debouncedSearch && (
					<div className={styles.emptyCard}>
						<span className={styles.emptyTitle}>{t('The new school year starts here')}</span>
						<span className={styles.emptyText}>
							{t('No Junior Company is listed yet — yours could be the first.')}
						</span>
						<Link className={styles.emptyBtn} to={TRoutes.SIGN_UP}>
							{t('Found your Junior Company')}
						</Link>
					</div>
				)}
			</div>
			<div className={styles.subHeader}>
				<Pagination limit={limit} page={page} label={t('companies')} countAll={countAll} onChange={setPage} />
			</div>

			<PublicFooter />

			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}

const mapStateToProps = state => ({
	branch: state.options.branch,
	schoolTypes: state.options.schoolTypes
})

export default connect(mapStateToProps)(CompaniesPresentation)
